import { StrictMode, useCallback, useEffect, useState } from 'react'
import type { VFC } from 'react'
import { SlideType } from '@opallabs/concepts'
import { ShadowElevation } from '@opallabs/gem'
import { kuiperClassNames, Conditional } from '@opallabs/kuiper'
import {
  Sidebar as SidebarLayout,
  SidebarSection,
} from '@opallabs/lapidary-unstable'
import { trackCanvasSlideUpdate } from '../../Helpers/eventsTracking'
import { Changeset, ImageAlignment, ImageSize, Layout } from '../types'
import type { SlideEditorProps } from '../types'
import { AlignmentSection } from './AlignmentSection'
import { BackgroundAssetSection } from './BackgroundAssetSection'
import { LayoutSection } from './LayoutSection'
import { SizeSection } from './SizeSection'
import * as styles from './styles'
import { VisibilitySection } from './VisibilitySection'

/**
 * @note This implementation is currently only useful for the canvas slide type. It
 *   will need to be reworked when we port the calendar slide sidebars to
 *   Hubble.
 */
export const Sidebar: VFC<SlideEditorProps> = ({
  alignment,
  backgroundAssetReference,
  layout,
  onChange,
  presentationId,
  slideId,
  slideType = SlideType.Canvas,
  theme = 'light',
  updateSlide,
  uploadAsset,
  visible,
}) => {
  const [changes, setChanges] = useState<Changeset>({})

  useEffect(() => {
    updateSlide(presentationId, slideId, changes, onChange)
    // Intentionally disabling exhaustive-deps here, the only
    // change we want to respond to with an update is `changes`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes])

  const setBackgroundAssetReference = useCallback(
    (data: File | null) => {
      if (data) {
        uploadAsset(data, (assetData) => {
          setChanges({ background_asset_reference_id: assetData.data.id })
        })
      } else setChanges({ background_asset_reference_id: data })
    },
    [uploadAsset]
  )

  const setLayoutAndTrack = useCallback(
    (layout: Layout): void => {
      const updatedValue = {
        layout,
      }

      trackCanvasSlideUpdate({
        ...updatedValue,
        presentationId,
      })

      setChanges(updatedValue)
    },
    [presentationId]
  )

  return (
    <StrictMode>
      <SidebarLayout
        className={kuiperClassNames('slide-sidebar')}
        elevation={ShadowElevation.E0}
        styleOverrides={styles.container}
        theme={theme}
      >
        <Conditional condition={slideType === SlideType.Canvas}>
          <SidebarSection theme={theme}>
            <LayoutSection
              layout={layout}
              onChange={setLayoutAndTrack}
              theme={theme}
            />
          </SidebarSection>
        </Conditional>

        <Conditional
          condition={
            slideType === SlideType.Canvas && layout === Layout.ImageAndText
          }
        >
          <SidebarSection theme={theme}>
            <AlignmentSection
              alignment={alignment as ImageAlignment}
              onChange={(alignment: ImageAlignment): void =>
                setChanges({ alignment })
              }
              theme={theme}
            />
          </SidebarSection>
        </Conditional>

        <Conditional
          condition={slideType === SlideType.Canvas && layout == Layout.Image}
        >
          <SidebarSection theme={theme}>
            <SizeSection
              alignment={alignment as ImageSize}
              onChange={(alignment: ImageSize): void =>
                setChanges({ alignment })
              }
              theme={theme}
            />
          </SidebarSection>
        </Conditional>

        <SidebarSection>
          <BackgroundAssetSection
            backgroundAssetReference={backgroundAssetReference}
            onChange={setBackgroundAssetReference}
            theme={theme}
          />
        </SidebarSection>

        <SidebarSection>
          <VisibilitySection
            onChange={(is_visible_when_presenting: boolean): void =>
              setChanges({ is_visible_when_presenting })
            }
            theme={theme}
            visible={visible}
          />
        </SidebarSection>
      </SidebarLayout>
    </StrictMode>
  )
}
Sidebar.displayName = 'Hubble: Sidebar ← SlideEditor'
